import { FontFamilyComboBoxProps } from 'components/VisualStudioPlayground/FontFamily/Types'
import { useModals } from 'almanac-ui/Modal'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { FontsUploadModal } from 'components/FontsUploadModal/FontsUploadModal'
import { Item, Section } from '@react-stately/collections'
import Styles from './FontFamily.module.scss'
import { UploadCloud } from 'almanac-ui/icons'
import { CollectionElement } from '@react-types/shared'
import { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { GoogleFontLoader } from 'components/GoogleFontLoader/GoogleFontLoader'
import { LineSkeleton } from 'components/Skeleton/LineSkeleton'
import { FontWeightType } from '../VisualEditor/Types'
import { Button } from 'blaze-ui/Button'
import { FontsModal } from 'components/FontsModal/FontsModal'
import { ComboBoxContext } from 'components/ComboBox/ComboBoxContext'
import Settings from 'blaze-ui/icons/20/Settings'
import { GoogleFont } from 'stores/GoogleFonts/GoogleFont'
import { BlazeFontLoader } from 'components/BlazeFontLoader/BlazeFontLoader'
import { BlazeFont } from 'stores/BlazeFonts/BlazeFont'
import { CustomFont } from 'stores/CustomFonts/CustomFont'

const ManageButton = () => {
  const { openModal } = useModals()
  const { close } = useContext(ComboBoxContext)

  const openFontsModal = () => {
    close()
    openModal(FontsModal, {}, { newStack: true })
  }

  return (
    <div className={Styles.manageButton}>
      <Button size="xs" variant="ghost" onPress={openFontsModal} frontIcon={Settings}>
        Manage Fonts
      </Button>
    </div>
  )
}

export const FontFamilyComboBox = observer(
  ({
    onSelectionChange,
    fonts = [],
    brandKitFontFamilies = [],
    allowUpload = true,
    ...props
  }: FontFamilyComboBoxProps) => {
    const { openModal } = useModals()
    const { fonts: fontsStore } = useRootStore()
    const [popover, setPopover] = useState<HTMLDivElement>()
    const brandFonts = brandKitFontFamilies.map((fontFamily) => fontsStore.get(fontFamily)).filter(Boolean) as (
      | BlazeFont
      | GoogleFont
      | CustomFont
    )[]

    return (
      <ComboBox
        popoverRef={setPopover}
        {...props}
        onSelectionChange={(keyWithIndex) => {
          const newFamily = (keyWithIndex as string)?.split('§')[0]
          if (!newFamily) return

          if (newFamily === 'upload') {
            return openModal(FontsUploadModal)
          }

          const weights: Array<FontWeightType> = fontsStore.get(newFamily)?.weights || [400]

          onSelectionChange(newFamily, weights)
        }}
      >
        {
          [
            fonts.length === 0 && allowUpload && (
              <Item key="upload" textValue="Upload Your Own">
                <span className={Styles.uploadItem}>
                  <UploadCloud /> Upload your own font
                </span>
              </Item>
            ),
            brandFonts.length > 0 && (
              <Section key="brand fonts" title={<div className={Styles.sectionHeader}>Brand Fonts</div>}>
                {brandFonts.map((brandFont, index) => {
                  const isLoaded = brandFont.loadingState === 'loaded'

                  return (
                    <Item key={`${brandFont.name}§${index}`} textValue={brandFont.name}>
                      <span
                        style={{
                          fontFamily: `'${brandFont.name}'`,
                          fontSize: 18,
                          color: !isLoaded ? 'transparent' : undefined,
                        }}
                        className={Styles.fontFamily}
                      >
                        {brandFont.name} {!isLoaded && <LineSkeleton className={Styles.fontFamilyShimmer} />}
                      </span>
                      {brandFont instanceof GoogleFont && <GoogleFontLoader family={brandFont.name} root={popover} />}
                      {brandFont instanceof BlazeFont && <BlazeFontLoader name={brandFont.name} root={popover} />}
                    </Item>
                  )
                })}
              </Section>
            ),

            fonts.length > 0 && (
              <Section
                key="your fonts"
                title={
                  <div className={Styles.sectionHeader}>
                    Your Fonts
                    <ManageButton />
                  </div>
                }
              >
                {[
                  ...fonts.map((font) => (
                    <Item key={font.name} textValue={font.name}>
                      <span className={Styles.customFontPreview} style={{ fontFamily: `'${font.name}'`, fontSize: 18 }}>
                        {font.name}
                      </span>
                    </Item>
                  )),
                  <Item key="upload" textValue="Upload Your Own">
                    <span className={Styles.uploadItem}>
                      <UploadCloud /> Upload your own font
                    </span>
                  </Item>,
                ]}
              </Section>
            ),
            <Section key="blaze fonts" title="Blaze Fonts">
              {fontsStore.list(['blaze', 'google']).map((font) => {
                const isLoaded = font.loadingState === 'loaded'

                return (
                  <Item key={font.name} textValue={font.name}>
                    {font instanceof BlazeFont && <BlazeFontLoader name={font.name} root={popover} />}
                    {font instanceof GoogleFont && <GoogleFontLoader family={font.name} root={popover} />}
                    <span
                      style={{
                        fontFamily: `'${font.name}'`,
                        fontSize: 18,
                        color: !isLoaded ? 'transparent' : undefined,
                        fontWeight: font.weights[0],
                      }}
                      className={Styles.fontFamily}
                    >
                      {font.name} {!isLoaded && <LineSkeleton className={Styles.fontFamilyShimmer} />}
                    </span>
                  </Item>
                )
              })}
            </Section>,
          ].filter(Boolean) as CollectionElement<HTMLElement>[]
        }
      </ComboBox>
    )
  },
)
